import axios, { RawAxiosRequestHeaders } from 'axios';

const bearer = document.head.querySelector('meta[name=access-token]')?.attributes.getNamedItem('content')?.value;
const csrf = document.head.querySelector('meta[name=csrf-token]')?.attributes.getNamedItem('content')?.value;

const headers = {
  'X-Requested-With': 'XMLHttpRequest',
} as RawAxiosRequestHeaders;

if (bearer) {
  headers['Authorization'] = `Bearer ${bearer}`;
}

if (csrf) {
  headers['X-CSRF-TOKEN'] = csrf;
}

const request = axios.create({
  headers,
  baseURL: '/api/',
});

export const webRequest = axios.create({ headers });
export const apiRequest = axios.create({ headers, baseURL: '/api/', });

// Add interceptors to all axios instances
const addInterceptors = (instance: any) => {
  instance.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      if (axios.isAxiosError(error)) {
        // Handle CSRF/authentication errors
        if (handleAuthError(error)) {
          return Promise.reject(error);
        }

        return Promise.reject(handleAxiosError(error));
      }

      return Promise.reject(handleUnexpectedError(error));
    }
  );
};

// Apply interceptors to all instances
addInterceptors(webRequest);
addInterceptors(apiRequest);

export const setAuthorizationToken = (token: any) => {
  request.defaults.headers.common['Authorization'] = token;
};

// Function to handle authentication/CSRF errors
function handleAuthError(error: any): boolean {
  const status = error?.response?.status;
  if (status === 419 || status === 401) {
    const errorMessage = error?.response?.data?.message || '';
    const isCSRFError = status === 419 || errorMessage.toLowerCase().includes('csrf') || errorMessage.toLowerCase().includes('token');

    if (isCSRFError || status === 401) {
      if (window.location.pathname.includes('/guest/login')) {
        return true;
      }

      try {
        const $message = (window as any)['$message'];
        if ($message && typeof $message.warning === 'function') {
          $message.warning('Your session has expired. Redirecting to login...');
        }
      } catch {
      }

      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth-token');
        localStorage.removeItem('ACCESS-TOKEN');
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth-token');
        sessionStorage.removeItem('ACCESS-TOKEN');
      }

      try {
        fetch('/api/logout', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrf || ''
          }
        }).catch(() => {
        });
      } catch {
      }

      setTimeout(() => {
        window.location.href = '/guest/login';
      }, 1000);

      return true;
    }
  }
  return false;
}

function handleAxiosError(error: any) {
  let message = 'Unknown Request Error';
  const status = error?.response?.status;

  switch (status) {
    case 400:
      message = 'Bad Request Error';
      break;
    case 404:
      message = 'Not Found Error';
      break;
    case 500:
      message = 'Internal Server Error';
      break;
    case 403:
      message = 'Forbidden Error';
      break;
    case 409:
      message = 'Conflicting Error';
      break;
    case 422:
      return error?.response?.data;
    case 419:
      message = 'CSRF Token Expired';
      break;
    case 401:
      message = 'Unauthorized';
      break;
    default:
      break;
  }
  return { message, status };
}

// @ts-ignore
function handleUnexpectedError(error: any) {
  return { message: 'Unknown Error' };
}

request.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      // Handle CSRF/authentication errors
      if (handleAuthError(error)) {
        return Promise.reject(error);
      }

      return Promise.reject(handleAxiosError(error));
    }

    return Promise.reject(handleUnexpectedError(error));
  }
);

export default request;
